/* ------------------------------------------------------------------
 * Product image zoom
 *
 * Desktop (viewport wider than MOBILE_MAX_WIDTH): hovering the main
 * product image opens a pan-to-zoom overlay that follows the cursor.
 * No button, no click needed - mouseleave closes it.
 *
 * Mobile (viewport at or below MOBILE_MAX_WIDTH): tapping the main
 * product image opens a full-screen lightbox with +/- zoom controls.
 * Tapping the backdrop (anywhere outside the image) closes it.
 * ------------------------------------------------------------------ */

let currentOverlay = null;
let currentZoomImage = null;
let currentMobileModal = null;
const ZOOM_RATIO = 2;
const MOBILE_MAX_WIDTH = 767;
const MOBILE_ZOOM_MIN = 1;
const MOBILE_ZOOM_MAX = 3;
const MOBILE_ZOOM_STEP = 0.5;

function isMobileViewport() {
  return window.matchMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`).matches;
}

/* ---------------------- Desktop hover-to-zoom ---------------------- */

function closeMagnify(image) {
  if (currentOverlay) {
    currentOverlay.remove();
    currentOverlay = null;
  }
  if (image) {
    image.style.opacity = "100%";
  }
  currentZoomImage = null;
}

function createOverlay(image) {
  const overlayImage = document.createElement("img");
  overlayImage.setAttribute("src", `${image.src}`);
  const overlay = document.createElement("div");
  prepareOverlay(overlay, overlayImage);

  image.style.opacity = "50%";
  toggleLoadingSpinner(image);

  overlayImage.onload = () => {
    toggleLoadingSpinner(image);
    image.parentElement.insertBefore(overlay, image);
    image.style.opacity = "100%";
  };

  return overlay;
}

function prepareOverlay(container, image) {
  container.setAttribute("class", "image-magnify-full-size");
  container.setAttribute("aria-hidden", "true");
  container.style.backgroundImage = `url('${image.src}')`;
  container.style.backgroundColor = "var(--gradient-background)";
}

function toggleLoadingSpinner(image) {
  const loadingSpinner =
    image.parentElement.parentElement.querySelector(`.loading__spinner`);
  if (loadingSpinner) {
    loadingSpinner.classList.toggle("hidden");
  }
}

function moveWithHover(image, event, zoomRatio) {
  const ratio = image.height / image.width;
  const container = event.target.getBoundingClientRect();
  const xPosition = event.clientX - container.left;
  const yPosition = event.clientY - container.top;
  const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
  const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

  if (currentOverlay) {
    currentOverlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    currentOverlay.style.backgroundSize = `${image.width * zoomRatio}px`;
  }
}

function openMagnify(image, zoomRatio) {
  if (currentOverlay && currentZoomImage === image) return;
  if (currentOverlay) closeMagnify(currentZoomImage);

  currentZoomImage = image;
  currentOverlay = createOverlay(image);
  currentOverlay.onmousemove = (event) =>
    moveWithHover(image, event, zoomRatio);
  currentOverlay.onmouseleave = () => closeMagnify(image);
}

function enableDesktopHoverZoom(image, zoomRatio) {
  image.addEventListener("mouseenter", () => {
    if (isMobileViewport()) return;
    openMagnify(image, zoomRatio);
  });
  image.addEventListener("mouseleave", (event) => {
    if (isMobileViewport()) return;
    // Only close if the cursor didn't move onto the overlay itself.
    if (!currentOverlay || !currentOverlay.contains(event.relatedTarget)) {
      closeMagnify(image);
    }
  });
}

/* ---------------------- Mobile tap-to-zoom modal ---------------------- */

function openMobileZoomModal(image) {
  if (currentMobileModal) return;

  const backdrop = document.createElement("div");
  backdrop.className = "product-zoom-modal";
  backdrop.setAttribute("role", "dialog");
  backdrop.setAttribute("aria-modal", "true");
  backdrop.setAttribute("aria-label", "Zoomed product image");

  const scrollArea = document.createElement("div");
  scrollArea.className = "product-zoom-modal__scroll";

  const zoomImg = document.createElement("img");
  zoomImg.src = image.currentSrc || image.src;
  zoomImg.alt = image.alt || "";
  zoomImg.className = "product-zoom-modal__image";

  scrollArea.appendChild(zoomImg);
  backdrop.appendChild(scrollArea);

  const controls = document.createElement("div");
  controls.className = "product-zoom-modal__controls";

  const minusBtn = document.createElement("button");
  minusBtn.type = "button";
  minusBtn.className = "product-zoom-modal__btn";
  minusBtn.setAttribute("aria-label", "Zoom out");
  minusBtn.textContent = "−";

  const plusBtn = document.createElement("button");
  plusBtn.type = "button";
  plusBtn.className = "product-zoom-modal__btn";
  plusBtn.setAttribute("aria-label", "Zoom in");
  plusBtn.textContent = "+";

  controls.appendChild(minusBtn);
  controls.appendChild(plusBtn);
  backdrop.appendChild(controls);

  document.body.appendChild(backdrop);
  document.body.classList.add("product-zoom-modal-open");
  currentMobileModal = backdrop;

  let zoom = MOBILE_ZOOM_MIN;

  function centerScroll() {
    requestAnimationFrame(() => {
      scrollArea.scrollLeft =
        (scrollArea.scrollWidth - scrollArea.clientWidth) / 2;
      scrollArea.scrollTop =
        (scrollArea.scrollHeight - scrollArea.clientHeight) / 2;
    });
  }

  function applyZoom() {
    zoomImg.style.width = `${zoom * 100}%`;
    minusBtn.disabled = zoom <= MOBILE_ZOOM_MIN;
    plusBtn.disabled = zoom >= MOBILE_ZOOM_MAX;
    centerScroll();
  }
  applyZoom();

  function closeModal() {
    backdrop.remove();
    document.body.classList.remove("product-zoom-modal-open");
    document.removeEventListener("keydown", onKeydown);
    if (currentMobileModal === backdrop) currentMobileModal = null;
  }
  backdrop._closeZoomModal = closeModal;

  function onKeydown(event) {
    if (event.key === "Escape") closeModal();
  }
  document.addEventListener("keydown", onKeydown);

  plusBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    zoom = Math.min(MOBILE_ZOOM_MAX, zoom + MOBILE_ZOOM_STEP);
    applyZoom();
  });
  minusBtn.addEventListener("click", (event) => {
    event.stopPropagation();
    zoom = Math.max(MOBILE_ZOOM_MIN, zoom - MOBILE_ZOOM_STEP);
    applyZoom();
  });

  // Tapping the backdrop (not the image, not the controls) closes it.
  backdrop.addEventListener("click", (event) => {
    if (event.target === backdrop || event.target === scrollArea) {
      closeModal();
    }
  });
}

function enableMobileTapZoom(image) {
  image.addEventListener("click", (event) => {
    if (!isMobileViewport()) return;
    event.preventDefault();
    openMobileZoomModal(image);
  });
}

/* ---------------------- Wiring ---------------------- */

function initProductMagnify(zoomRatio) {
  document.querySelectorAll(".image-magnify-hover").forEach((image) => {
    if (image.dataset.magnifyBound === "true") return;
    image.dataset.magnifyBound = "true";
    enableDesktopHoverZoom(image, zoomRatio);
    enableMobileTapZoom(image);
  });
}

initProductMagnify(ZOOM_RATIO);

document.addEventListener("shopify:section:load", () => {
  initProductMagnify(ZOOM_RATIO);
});

document.addEventListener("fuzion-product-gallery-swiper-ready", () => {
  initProductMagnify(ZOOM_RATIO);
});

window.addEventListener("resize", () => {
  // Crossing the mobile/desktop breakpoint while a zoom UI is open -
  // close whichever one is open rather than leaving it in a mismatched state.
  if (isMobileViewport() && currentOverlay) {
    closeMagnify(currentZoomImage);
  }
  if (!isMobileViewport() && currentMobileModal) {
    currentMobileModal._closeZoomModal?.();
  }
});
